import { PropertyHelper } from "@/helpers/property.helper";
import {
  HttpErrorResponse,
  HttpEventType,
  HttpResponse,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import {
  DocumentIdentifier,
  PhotoCategory,
  PhotosService,
  PropertiesManagementService,
  PropertyCategory,
  RealProperty,
  VaultService,
} from "@eemo/api-client";
import { BehaviorSubject, Observable, Subject, retry } from "rxjs";

export class FileToUpload {
  file: File;
  name: string;
}

export enum FileUploadType {
  PHOTO,
  DOC,
  SCAN_DOC,
}

export class UploadOptionsDoc {
  category: PropertyCategory;
  type: DocumentType;
  linkObjectId?: string;
}

export class UploadOptionsPhoto {
  category: PhotoCategory;
}

class InternalUpload {
  filename: string;
  progress: BehaviorSubject<number>;
  req: Observable<any>;
  started: boolean;
  completed: boolean;
}

@Injectable()
export class FileUploadService {
  readonly maxConcurrentUploads = 2;

  public createdUid = new Subject<string>();

  private uploads: InternalUpload[];

  constructor(
    private vaultService: VaultService,
    private propertiesManagementService: PropertiesManagementService,
    private photoService: PhotosService,
    private propHelper: PropertyHelper
  ) {}

  public upload(
    type: FileUploadType,
    props: RealProperty[],
    options: any,
    files: Set<FileToUpload>
  ): { [filename: string]: { progress: Observable<number> } } {
    if (!props || props.length === 0) {
      throw new Error("Invalid or empty prop Array");
    }
    this.uploads = [];
    // this will be the our resulting map
    const status: { [filename: string]: { progress: Observable<number> } } = {};

    files.forEach((fileData) => {
      const filename = this.propHelper.getDefaultNameFromFilename(
        fileData.name
      );

      // create a http-post request and pass the form
      // tell it to report the upload progress
      let req;
      if (props.length > 1) {
        const propsId = props.map((p) => p.uid);
        // add multiple by property maanagement service
        switch (type) {
          case FileUploadType.DOC:
          case FileUploadType.SCAN_DOC:
            req = this.propertiesManagementService.addRealPropertiesDoc(
              propsId.join(","),
              DocumentIdentifier.NONE,
              options.type,
              options.category,
              filename,
              fileData.file,
              null,
              "events",
              true
            );
            break;
          default:
            req = this.propertiesManagementService.addRealPropertiesPhoto(
              propsId.join(","),
              filename,
              options.category,
              fileData.file,
              "events",
              true
            );
        }
      } else {
        switch (type) {
          case FileUploadType.DOC:
          case FileUploadType.SCAN_DOC:
            req = this.vaultService.addRealPropertyDocument(
              props[0].uid,
              DocumentIdentifier.NONE,
              options.type,
              options.category,
              filename,
              fileData.file,
              null,
              options.linkObjectId,
              "events",
              true
            );
            break;
          default:
            req = this.photoService.addRealPropertyPhoto(
              props[0].uid,
              filename,
              options.category,
              fileData.file,
              "events",
              true
            );
        }
      }
      // create a new progress-subject for every file
      const progress = new BehaviorSubject<number>(-1);

      this.uploads.push({
        filename: fileData.file.name,
        progress,
        req,
        started: false,
        completed: false,
      });
      // Save every progress-observable in a map of all observables
      status[fileData.file.name] = {
        progress: progress.asObservable(),
      };
    });
    this.manageUploads();

    // return the map of progress.observables
    return status;
  }

  private manageUploads() {
    // we want only a fiw upload at the same time
    const current = this.uploads.filter(
      (u) => u.started && !u.completed
    ).length;
    const uploadsToStart = this.maxConcurrentUploads - current;
    for (let i = 0; i < uploadsToStart; i++) {
      // start the first upload available
      const upload = this.uploads.find((u) => !u.started);
      if (upload) {
        this.startUpload(upload);
      }
    }
  }

  private startUpload(upload: InternalUpload) {
    // send the http-request and subscribe for progress-updates
    const progress = upload.progress;
    upload.started = true;
    // add retry in case of errors (to manage 499 errors on ingress controller)
    upload.req.pipe(retry(3)).subscribe({
      next: (event) => {
        if (event.type === HttpEventType.UploadProgress) {
          // calculate the progress percentage
          const percentDone = Math.round((100 * event.loaded) / event.total);

          // pass the percentage into the progress-stream
          progress.next(percentDone);
        } else if (event instanceof HttpResponse) {
          // Close the progress-stream if we get an answer form the API
          // The upload is complete
          progress.complete();
          upload.completed = true;
          this.manageUploads();
          this.createdUid.next(event.body);
        }
      },
      error: (err: HttpErrorResponse) => {
        progress.error(err.message);
        progress.complete();
        upload.completed = true;
        this.manageUploads();
      },
    });
  }
}
